import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, BookOpen, ChevronRight, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Content data for different topics
const CONTENT_DATA = {
  semaglutide: {
    title: 'About Semaglutide',
    subtitle: 'GLP-1 Receptor Agonist',
    sections: [
      {
        title: 'What is Semaglutide?',
        content: 'Semaglutide is a GLP-1 receptor agonist that may help support appetite regulation and weight management when combined with lifestyle changes. It works by mimicking a hormone that targets areas of the brain involved in appetite control.'
      },
      {
        title: 'How It Works',
        content: 'By activating GLP-1 receptors, Semaglutide may help reduce hunger signals and increase feelings of fullness after meals. This can support individuals in making sustainable dietary changes.'
      },
      {
        title: 'Administration',
        content: 'Typically administered as a weekly injection or oral tablet, depending on the specific formulation and provider recommendations.'
      }
    ],
    disclaimer: 'Compounded Semaglutide is not FDA approved for weight loss. All treatment decisions require provider review and approval. Individual results may vary.'
  },
  tirzepatide: {
    title: 'About Tirzepatide',
    subtitle: 'Dual GLP-1/GIP Receptor Agonist',
    sections: [
      {
        title: 'What is Tirzepatide?',
        content: 'Tirzepatide is a dual-action medication that activates both GLP-1 and GIP receptors. This dual mechanism may provide comprehensive metabolic support for weight management goals.'
      },
      {
        title: 'How It Works',
        content: 'By targeting two receptor pathways instead of one, Tirzepatide may offer enhanced support for appetite control and metabolic function. The dual action is designed to work synergistically.'
      },
      {
        title: 'Administration',
        content: 'Typically administered as a weekly injection under provider supervision, with dosing adjusted based on individual response and tolerance.'
      }
    ],
    disclaimer: 'Compounded Tirzepatide is not FDA approved for weight loss. All treatment decisions require provider review and approval. Individual results may vary.'
  },
  sermorelin: {
    title: 'About Sermorelin',
    subtitle: 'Growth Hormone Releasing Peptide',
    sections: [
      {
        title: 'What is Sermorelin?',
        content: 'Sermorelin is a peptide designed to support the body\'s natural growth hormone signaling pathways. Many individuals exploring metabolic optimization consider it for recovery and energy support.'
      },
      {
        title: 'Potential Benefits',
        content: 'Sermorelin may support physical recovery, sleep quality, energy levels, and body composition over time when used as part of a comprehensive wellness program.'
      },
      {
        title: 'Administration',
        content: 'Typically administered via injection, often in the evening, under provider guidance. Treatment protocols vary based on individual goals and response.'
      }
    ],
    disclaimer: 'Sermorelin is not FDA approved for weight loss. This information is for educational purposes only. All treatment decisions require provider review and approval.'
  },
  general: {
    title: 'Medical Weight Loss Options',
    subtitle: 'Provider-Guided Treatment Paths',
    sections: [
      {
        title: 'What We Offer',
        content: 'Bryan 2.0 Forecast helps connect you with provider-guided medical weight loss options including GLP-1 medications (Semaglutide, Tirzepatide) and advanced peptide therapies.'
      },
      {
        title: 'How It Works',
        content: 'Take our AI-powered quiz to receive personalized recommendations, then connect with Bryan to discuss which treatment path may align with your goals and medical history.'
      },
      {
        title: 'What to Expect',
        content: 'All treatments require provider review and medical eligibility assessment. Our team will guide you through the process and answer any questions you have along the way.'
      }
    ],
    disclaimer: 'All treatment options require provider review and approval. Compounded medications are not FDA approved. Results vary by individual.'
  }
};

const LearnMoreModal = ({ isOpen, onClose, topic = 'general', source, leadId = null }) => {
  const navigate = useNavigate();
  const content = CONTENT_DATA[topic] || CONTENT_DATA.general;

  // Track that user viewed this content
  React.useEffect(() => {
    if (isOpen && source) {
      axios.post(`${API}/events/track`, {
        event_type: 'learn_more_view',
        topic: topic,
        source: source,
        lead_id: leadId
      }).catch(() => { /* Event tracking non-critical */ });
    }
  }, [isOpen, topic, source, leadId]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-2xl max-h-[85vh] bg-slate-950/95 backdrop-blur-xl border border-cyan-500/20 rounded-2xl shadow-[0_0_50px_rgba(34,211,238,0.3)] overflow-hidden flex flex-col"
          data-testid="learn-more-modal"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-cyan-500/20 bg-gradient-to-r from-cyan-500/10 to-transparent">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                <BookOpen className="h-6 w-6 text-[#22D3EE]" />
              </div>
              <div>
                <h3 className="font-heading text-2xl font-bold text-white">{content.title}</h3>
                <p className="text-sm text-cyan-400">{content.subtitle}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors"
              data-testid="learn-more-close"
            >
              <X className="h-5 w-5 text-slate-400 hover:text-white" />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {content.sections.map((section, idx) => (
              <div key={idx} className="space-y-2">
                <h4 className="font-heading text-lg font-bold text-white flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-cyan-500" />
                  {section.title}
                </h4>
                <p className="text-slate-300 leading-relaxed pl-7">{section.content}</p>
              </div>
            ))}

            {/* Disclaimer */}
            <div className="bg-slate-900/60 border border-amber-500/20 rounded-xl p-4 mt-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-400 leading-relaxed">
                  <strong className="text-amber-400">Important:</strong> {content.disclaimer}
                </p>
              </div>
            </div>
          </div>

          {/* Footer CTAs */}
          <div className="p-6 border-t border-cyan-500/20 bg-slate-950/80">
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => {
                  onClose();
                  navigate('/quiz');
                }}
                className="flex-1 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-400 hover:to-cyan-500 text-white rounded-xl py-5 font-bold h-auto"
                data-testid="learn-more-start-forecast"
              >
                Get My Personalized Forecast
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                onClick={onClose}
                variant="outline"
                className="flex-1 border-slate-700 hover:border-cyan-500/50 bg-slate-900/60 hover:bg-cyan-500/10 text-slate-300 hover:text-cyan-300 rounded-xl py-5 font-bold h-auto"
                data-testid="learn-more-close-btn"
              >
                Close
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default LearnMoreModal;
